import type {
  Amount,
  AmountValue,
  ExecutionOrderProtocol,
  ExecutionOrderStrategy,
  OrderSize,
  PriceValue,
  TraderProfileForOrder,
} from '@/types/api/order'
import type { OrderSide, OrderType } from '@/types/enums/order'

export enum OrderUpdateEventType {
  Request = 'request',
  Approve = 'approve',
  Reject = 'reject',
  Cancel = 'cancel',
  Execution = 'execution',
  CreateExecutionOrder = 'execution_order_create',
  CancelExecutionOrder = 'execution_order_cancel',
  ReplaceExecutionOrder = 'execution_order_replace',
  FlaggedForReview = 'flagged_for_review',
  RFQCreate = 'rfq_create',
}

interface OrderUpdateEventBase {
  event_type: OrderUpdateEventType
  time: string
  order_id: string
  client_order_id?: string
  is_group_order?: boolean
  group_members?: { moment_order_id: string }[]
}

export interface OrderRequestEvent extends OrderUpdateEventBase {
  event_type: OrderUpdateEventType.Request
  risk_group_id: string
  isin: string
  cusip: string
  side: OrderSide
  amount: OrderSize
  limit_price: number
  account_id: string
  type: OrderType
  extended_hours: boolean
  annotation: string
}

interface OrderApproveEvent extends OrderUpdateEventBase {
  event_type: OrderUpdateEventType.Approve
}

enum OrderRejectCode {
  Unspecified = 600,
}

export interface OrderRejectEvent extends OrderUpdateEventBase {
  event_type: OrderUpdateEventType.Reject
  reject_code: OrderRejectCode
  reject_reason: string
}

enum OrderCancelCode {
  Unknown = 700,
  Expiry = 701,
  ApiRequest = 702,
  TradeBlock = 703,
  Unsolicited = 704,
}

export interface OrderCancelEvent extends OrderUpdateEventBase {
  event_type: OrderUpdateEventType.Cancel
  cancel_code: OrderCancelCode
  cancel_reason: string
}

export interface OrderExecutionEvent extends OrderUpdateEventBase {
  event_type: OrderUpdateEventType.Execution
  /** The execution order this fill belongs to. */
  execution_order_id: string
  accrued_interest: number
  amount: AmountValue
  counterparty: string
  counterparty_clearing_firm: string
  net_money: number
  pre_fee_net_money: number
  pre_fee_price?: number
  price: number
  settlement_date: string
  snapshot_id?: string
  trade_date: string
  venue: string
  venue_execution_id: string
  ytm: number
  ytw: number
}

export interface CreateExecutionOrder extends OrderUpdateEventBase {
  event_type: OrderUpdateEventType.CreateExecutionOrder
  time: string
  execution_order_id: string
  client_execution_order_id: string
  amount: Amount
  limit_price: number | null
  protocol: ExecutionOrderProtocol
  strategy: ExecutionOrderStrategy
  rfq_metadata: {
    moment_rfq_id: string
    contra_quote_id: string | null
  } | null
  suppress_pmp_check: boolean
  /**
   * Present on trader-initiated execution orders (e.g. custodian protocol).
   * Used to attribute the request to the trader who routed it.
   */
  trader_profile?: TraderProfileForOrder
}

export interface ReplaceExecutionOrder extends OrderUpdateEventBase {
  event_type: OrderUpdateEventType.ReplaceExecutionOrder
  time: string
  execution_order_id: string
  client_execution_order_id: string
  execution_order_replace_request_id: string
  previous_execution_order_id: string
  quantity: OrderSize
  previous_quantity: OrderSize
  order_type: string
  previous_order_type: string
  limit_price?: PriceValue | null
  previous_limit_price?: PriceValue | null
}

// Alone among the order updates, this payload names only the execution order —
// orders-api sends no `order_id` on it. The parent order is on the message
// envelope (`OrderUpdateEventMessage.order`), which is where to read it.
export interface CancelExecutionOrder
  extends Omit<OrderUpdateEventBase, 'order_id'> {
  event_type: OrderUpdateEventType.CancelExecutionOrder
  time: string
  execution_order_id: string
  client_execution_order_id: string
  cancel_reason: string
  cancel_code: number
}

export interface FlaggedForReviewEvent {
  event_type: OrderUpdateEventType.FlaggedForReview
  time: string
  order_id: string
  failed_risk_controls: { message: string }[] | null
}

interface RFQCreatedEvent extends OrderUpdateEventBase {
  event_type: OrderUpdateEventType.RFQCreate
  time: string
  order_id: string
  rfq_id: string
}

export type OrderUpdateEvent =
  | OrderRequestEvent
  | OrderApproveEvent
  | OrderRejectEvent
  | OrderCancelEvent
  | OrderExecutionEvent
  | CreateExecutionOrder
  | ReplaceExecutionOrder
  | CancelExecutionOrder
  | FlaggedForReviewEvent
  | RFQCreatedEvent
